import { memo } from 'react'

import { LoadingButton } from '@mui/lab'
import { Alert, Box, Button, styled } from '@mui/material'
import { SmepEvent, SmepEventConfig } from '@nativewaves/nw-cloud-sdk/smep'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'motion/react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { appearance } from '@shared/utils/support'
import { workspaceQueryKeys } from 'services/queryKeys'

import { Panel as GlobalAudioPanel } from './GlobalAudio/Panel'
import { ScheduleFields } from './ScheduleFields'
import { Panel as TopLevelPanel } from './TopLevel/Panel'
import { useConfigForm } from './useConfigForm'
import { Panel as ViewsPanel } from './Views/Panel'

const StyledForm = styled(motion.form)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  padding: theme.spacing(3),
  height: '100%',
  overflowY: 'auto',
}))

export type ConfigFormProps = {
  event: SmepEvent
  config: SmepEventConfig
  workspaceId: string
  /** Called after a successful save (e.g. to collapse an inline editor back to read mode). */
  onSaved?: () => void
  /** Called when the user cancels editing; when set, a Cancel button is shown next to Save. */
  onCancel?: () => void
  /** Also surface + persist the event schedule (start/end) at the top of the form. */
  showSchedule?: boolean
}

const ConfigFormRaw = ({ event, config, workspaceId, onSaved, onCancel, showSchedule }: ConfigFormProps) => {
  const { t } = useTranslation(['common', 'domain'])

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))
  const repositoryId = workspaceQuery.data?.contentRepositoryId

  const { formMethods, handleSubmit, isSaving, externalChange, reloadFromServer } = useConfigForm({
    event,
    config,
    onSaved,
    includeSchedule: showSchedule,
  })

  const actions = (
    <>
      {onCancel && (
        <Button type="button" variant="text" disabled={isSaving} onClick={onCancel}>
          {t('domain:Workspace.Event.cancel')}
        </Button>
      )}
      <Button
        type="button"
        variant="outlined"
        disabled={!formMethods.formState.isDirty || isSaving}
        onClick={() => formMethods.reset()}
      >
        {t('reset')}
      </Button>
      <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!formMethods.formState.isDirty}>
        {t('save')}
      </LoadingButton>
    </>
  )

  return (
    <FormProvider {...formMethods}>
      <StyledForm onSubmit={handleSubmit} variants={appearance} initial="hidden" animate="visible">
        {externalChange && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reloadFromServer}>
                {t('domain:Workspace.Event.ConfigEditor.reload')}
              </Button>
            }
          >
            {t('domain:Workspace.Event.ConfigEditor.externalChange')}
          </Alert>
        )}
        {showSchedule ? (
          <ScheduleFields actions={actions} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>{actions}</Box>
        )}
        <TopLevelPanel />
        <ViewsPanel repositoryId={repositoryId} />
        <GlobalAudioPanel repositoryId={repositoryId} />
      </StyledForm>
    </FormProvider>
  )
}

export const ConfigForm = memo(ConfigFormRaw)
